"use client";

import { format, isSameDay, isToday, startOfWeek } from "date-fns";
import {
  type KeyboardEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { clamp, cn } from "@/lib/utils";

import { TONE_ACTIVE, useCalendarContext } from "../calendar-context";
import {
  type DisabledSpan,
  type EventIndex,
  getDisabledSpansForDay,
  getFreeSlots,
} from "../event-layout";
import { type CalendarEvent } from "../types";
import { formatWeekday, getDayKey, getVisibleWeekDays } from "../utils";

import { TimeGutter } from "./time-gutter";
import { WeekAllDayRow } from "./week-all-day-row";
import { WeekDayColumn } from "./week-day-column";

// =============================================================================

const NO_EVENTS: CalendarEvent[] = [];

// Columns keep a usable width: below it the grid scrolls horizontally (phones)
const GUTTER_REM = 4;
const MIN_COLUMN_REM = 5.5;

type SlotRef = { dayKey: string; minutes: number };

type Column = {
  day: Date;
  key: string;
  events: CalendarEvent[];
  disabledSpans: DisabledSpan[];
  freeSlots: number[];
};

function slotAt(column: Column, index: number): SlotRef | undefined {
  const minutes = column.freeSlots[index];
  return minutes === undefined ? undefined : { dayKey: column.key, minutes };
}

/**
 * Time grid of the week. A single scroll container for both axes: the day
 * headers stick to the top, the hours to the left. The free slots form one tab
 * stop, arrows move between them.
 */
export function WeekView({ eventIndex }: { eventIndex: EventIndex }) {
  const {
    date,
    locale,
    weekStartsOn,
    hiddenWeekdays,
    minHour,
    maxHour,
    slotDuration,
    businessHours,
    disabledRanges,
    onSlotClick,
    tone,
  } = useCalendarContext();

  const weekStart = startOfWeek(date, { weekStartsOn }).getTime();
  const days = useMemo(
    () => getVisibleWeekDays(new Date(weekStart), weekStartsOn, hiddenWeekdays),
    [weekStart, weekStartsOn, hiddenWeekdays]
  );

  const canClickSlots = !!onSlotClick;
  const columns = useMemo(
    () =>
      days.map((day): Column => {
        const key = getDayKey(day);
        const events = eventIndex.timedByDay.get(key) ?? NO_EVENTS;
        const disabledSpans = getDisabledSpansForDay(
          day,
          disabledRanges,
          minHour,
          maxHour
        );
        return {
          day,
          key,
          events,
          disabledSpans,
          freeSlots: canClickSlots
            ? getFreeSlots(
                day,
                events,
                disabledSpans,
                minHour,
                maxHour,
                slotDuration
              )
            : [],
        };
      }),
    [
      days,
      eventIndex,
      disabledRanges,
      minHour,
      maxHour,
      slotDuration,
      canClickSlots,
    ]
  );

  // ---------------------------------------------------------------------------
  // Roving tab stop over the free slots

  const [activeSlot, setActiveSlot] = useState<SlotRef | null>(null);
  const focusKey = getDayKey(date);
  const tabStop = useMemo((): SlotRef | null => {
    if (
      activeSlot &&
      columns.some(
        (column) =>
          column.key === activeSlot.dayKey &&
          column.freeSlots.includes(activeSlot.minutes)
      )
    ) {
      return activeSlot;
    }
    const column =
      columns.find((c) => c.key === focusKey && c.freeSlots.length) ??
      columns.find((c) => c.freeSlots.length);
    return column ? (slotAt(column, 0) ?? null) : null;
  }, [activeSlot, columns, focusKey]);

  const gridRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Keys typed in a portaled popover are not for the grid
    if (!event.currentTarget.contains(event.target as Node)) return;
    const slot = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-slot-min]"
    );
    if (!slot) return;
    const colIndex = columns.findIndex((c) => c.key === slot.dataset.slotDay);
    const column = columns[colIndex];
    if (!column) return;
    const minutes = Number(slot.dataset.slotMin);
    const index = column.freeSlots.indexOf(minutes);

    let target: SlotRef | undefined;
    switch (event.key) {
      case "ArrowUp":
        target = slotAt(column, index - 1);
        break;
      case "ArrowDown":
        target = slotAt(column, index + 1);
        break;
      case "Home":
        target = slotAt(column, 0);
        break;
      case "End":
        target = slotAt(column, column.freeSlots.length - 1);
        break;
      case "ArrowLeft":
      case "ArrowRight": {
        // Closest free slot of the next day that has one
        const step = event.key === "ArrowRight" ? 1 : -1;
        for (let i = colIndex + step; i >= 0 && i < columns.length; i += step) {
          const { key, freeSlots } = columns[i];
          if (!freeSlots.length) continue;
          const closest = freeSlots.reduce((best, candidate) =>
            Math.abs(candidate - minutes) < Math.abs(best - minutes)
              ? candidate
              : best
          );
          target = { dayKey: key, minutes: closest };
          break;
        }
        break;
      }
      default:
        return;
    }
    event.preventDefault();
    if (!target) return;
    const element = gridRef.current?.querySelector<HTMLElement>(
      `[data-slot-day="${target.dayKey}"][data-slot-min="${target.minutes}"]`
    );
    element?.focus({ preventScroll: true });
    element?.scrollIntoView({ block: "nearest", inline: "nearest" });
  };

  // ---------------------------------------------------------------------------
  // Initial scroll: an hour before now when today is on screen, else the
  // start of the working day

  const scrollRef = useRef<HTMLDivElement>(null);
  const [initialMinutes] = useState(() => {
    const now = new Date();
    return days.some((day) => isSameDay(day, now))
      ? now.getHours() * 60 + now.getMinutes() - 60
      : (businessHours?.startHour ?? minHour) * 60;
  });
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const grid = gridRef.current;
    if (!scroller || !grid) return;
    const ratio = clamp(
      (initialMinutes - minHour * 60) / ((maxHour - minHour) * 60),
      0,
      1
    );
    // The sticky headers cover exactly the space above the grid
    scroller.scrollTop = ratio * grid.offsetHeight;
  }, [initialMinutes, minHour, maxHour]);

  // ---------------------------------------------------------------------------

  const gridTemplateColumns = `repeat(${days.length}, minmax(0, 1fr))`;

  return (
    <div
      ref={scrollRef}
      data-lenis-prevent
      className="relative min-h-0 flex-1 overflow-auto"
    >
      <div
        className="flex flex-col"
        style={{
          minWidth: `max(100%, ${GUTTER_REM + days.length * MIN_COLUMN_REM}rem)`,
        }}
      >
        <div className="sticky top-0 z-20 flex h-14 border-b border-line bg-background">
          <div className="sticky left-0 z-10 w-16 shrink-0 border-r border-line bg-background" />
          <div className="grid flex-1" style={{ gridTemplateColumns }}>
            {days.map((day, i) => {
              const isDayToday = isToday(day);
              return (
                <div
                  key={day.getTime()}
                  aria-current={isDayToday ? "date" : undefined}
                  className={cn(
                    "flex min-w-0 flex-col items-center justify-center gap-0.5",
                    i > 0 && "border-l border-line"
                  )}
                >
                  <span className="text-xs text-muted-foreground">
                    {formatWeekday(day, locale)}
                  </span>
                  <span
                    className={cn(
                      "grid size-7 place-items-center rounded-full text-sm font-medium tabular-nums",
                      isDayToday ? TONE_ACTIVE[tone] : "text-foreground"
                    )}
                  >
                    {format(day, "d")}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <WeekAllDayRow days={days} eventIndex={eventIndex} />

        <div className="flex">
          <TimeGutter showNow={days.some((day) => isToday(day))} />
          <div
            ref={gridRef}
            className="grid flex-1"
            style={{ gridTemplateColumns }}
            onKeyDown={handleKeyDown}
          >
            {columns.map((column, i) => (
              <WeekDayColumn
                key={column.key}
                day={column.day}
                dayKey={column.key}
                events={column.events}
                disabledSpans={column.disabledSpans}
                freeSlots={column.freeSlots}
                tabStop={
                  tabStop?.dayKey === column.key ? tabStop.minutes : null
                }
                onSlotFocus={(dayKey, minutes) =>
                  setActiveSlot({ dayKey, minutes })
                }
                className={cn(i > 0 && "border-l border-line")}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
